import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useAnalyticsUtilities } from "@air/analytics";
import { Exclamation, Sync, TriangleDown } from "@air/next-icons";
import { Button } from "@air/primitive-button";
import { useAirModal } from "@air/provider-modal";
import { memo, useCallback, useState } from "react";
import { EnrichmentUpsellButton } from "~/components/AssetModalV2/InfoPanel/components/EnrichmentUpsellButton";
import { InformationContainer } from "~/components/AssetModalV2/InfoPanel/components/InformationContainer";
import { SmartTagKeywords } from "~/components/AssetModalV2/InfoPanel/DescriptionAndTags/SmartTagKeywords";
import { PlansModal } from "~/components/Modals/PlansModal/PlansModal";
import { INTELLIGENCE_STORAGE_LIMIT_MESSAGE } from "~/constants/upsells";
import { useSubscriptionContext } from "~/providers/SubscriptionProvider";
export const PrivateIntelligenceSection = /*#__PURE__*/ memo((param)=>{
    let { intelligence, title = "Summary & Smart Tags", showDisclaimer = true } = param;
    const [isSummaryExpanded, setIsSummaryExpanded] = useState(false);
    const [showPlansModal] = useAirModal(PlansModal);
    const { hasAddon, isEnterprise, isProcessing, hasContent, hasError, isUnsupported, summary, keywords, showStorageLimit, showUpgrade, showSection } = intelligence;
    const { track } = useAnalyticsUtilities();
    const { data: subscription } = useSubscriptionContext();
    const openUpgradeModal = useCallback(()=>{
        showPlansModal();
    }, [
        showPlansModal
    ]);
    const handleUpgradeClick = useCallback(()=>{
        track("Upgrade Button Clicked", {
            plan: (subscription === null || subscription === void 0 ? void 0 : subscription.plan) || "unknown"
        });
        openUpgradeModal();
    }, [
        track,
        subscription === null || subscription === void 0 ? void 0 : subscription.plan,
        openUpgradeModal
    ]);
    // Don't render if no content to show
    if (!showSection) return null;
    // Processing state
    if (isProcessing) {
        return /*#__PURE__*/ _jsxs("div", {
            children: [
                /*#__PURE__*/ _jsx("h4", {
                    className: "mb-3 text-12 font-semibold text-grey-12",
                    children: title
                }),
                /*#__PURE__*/ _jsx(InformationContainer, {
                    icon: /*#__PURE__*/ _jsx(Sync, {
                        className: "size-4 animate-spin text-grey-9"
                    }),
                    children: "Analyzing content to generate tags and a summary…"
                })
            ]
        });
    }
    // Unsupported file types
    if (isUnsupported) {
        return /*#__PURE__*/ _jsxs("div", {
            children: [
                /*#__PURE__*/ _jsx("h4", {
                    className: "mb-3 text-12 font-semibold text-grey-12",
                    children: title
                }),
                /*#__PURE__*/ _jsx(InformationContainer, {
                    icon: /*#__PURE__*/ _jsx(Exclamation, {
                        className: "size-4 text-grey-9"
                    }),
                    children: "Only image, video, and document formats are supported."
                })
            ]
        });
    }
    // Error state (for users with addon access)
    if (hasError && hasAddon) {
        return /*#__PURE__*/ _jsxs("div", {
            children: [
                /*#__PURE__*/ _jsx("h4", {
                    className: "mb-3 text-12 font-semibold text-grey-12",
                    children: title
                }),
                /*#__PURE__*/ _jsx(InformationContainer, {
                    icon: /*#__PURE__*/ _jsx(Exclamation, {
                        className: "size-4 text-grey-9"
                    }),
                    children: "We couldn't generate tags or a summary for this file."
                })
            ]
        });
    }
    // Storage limit exceeded
    if (showStorageLimit) {
        return /*#__PURE__*/ _jsxs("div", {
            children: [
                /*#__PURE__*/ _jsx("h4", {
                    className: "mb-3 text-12 font-semibold text-grey-12",
                    children: title
                }),
                /*#__PURE__*/ _jsxs(InformationContainer, {
                    icon: /*#__PURE__*/ _jsx(Exclamation, {
                        className: "size-4 text-grey-9"
                    }),
                    children: [
                        INTELLIGENCE_STORAGE_LIMIT_MESSAGE,
                        " ",
                        /*#__PURE__*/ _jsx("button", {
                            onClick: openUpgradeModal,
                            className: "text-blue-11 underline hover:text-blue-12",
                            type: "button",
                            children: "Click to upgrade"
                        })
                    ]
                })
            ]
        });
    }
    // Content section
    if (!hasContent && !hasAddon) return null;
    const summaryNeedsExpansion = summary && summary.length > 200;
    const words = (!hasAddon || showStorageLimit) && summary ? summary.split(" ") : [];
    const isPreview = words.length > 20;
    const truncatedSummary = isPreview ? words.slice(0, 20).join(" ") + "..." : summary;
    return /*#__PURE__*/ _jsxs("div", {
        children: [
            /*#__PURE__*/ _jsx("h4", {
                className: "mb-3 text-12 font-semibold text-grey-12",
                children: title
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-col [&>*+*]:mt-3",
                children: [
                    !!summary && /*#__PURE__*/ _jsxs("div", {
                        "data-testid": "AI_SUMMARY_TEXT",
                        className: "mb-2",
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: "text-12 text-grey-11 ".concat(!isSummaryExpanded ? "line-clamp-3" : ""),
                                children: truncatedSummary
                            }),
                            showUpgrade && !!isPreview && /*#__PURE__*/ _jsx(EnrichmentUpsellButton, {
                                onUpgrade: handleUpgradeClick
                            }),
                            (hasAddon || isEnterprise) && !!summaryNeedsExpansion && /*#__PURE__*/ _jsx(Button, {
                                onClick: ()=>setIsSummaryExpanded(!isSummaryExpanded),
                                appearance: "filled",
                                color: "grey",
                                size: "small",
                                className: "mt-2",
                                suffix: /*#__PURE__*/ _jsx(TriangleDown, {
                                    className: "size-3 transition-transform ".concat(isSummaryExpanded ? "rotate-180" : "")
                                }),
                                children: isSummaryExpanded ? "Show less" : "Show more"
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(SmartTagKeywords, {
                        hasAddonAccess: hasAddon,
                        isEnterprisePlan: isEnterprise,
                        onUpgradeClick: handleUpgradeClick
                    }),
                    showDisclaimer && (summary || (keywords === null || keywords === void 0 ? void 0 : keywords.length)) && /*#__PURE__*/ _jsx("div", {
                        className: "text-12 text-grey-9",
                        children: "Summary and smart tags are AI generated"
                    })
                ]
            })
        ]
    });
});
PrivateIntelligenceSection.displayName = "PrivateIntelligenceSection";
